import React from 'react';
import BackRow from 'components/BackRow/BackRow';
import Card from 'components/Card/Card';
import { EditText } from 'components/EditText/EditText';
import { post, dodelete, put, upload, get } from 'lib/comms';
import { DeleteDialog, MessageDialog } from 'components/Dialog/Dialog';
import EditBrowseTrust from 'components/EditBrowseTrust/EditBrowseTrust';
import { EditImage } from 'components/EditImage/EditImage';
import { UserContext } from '../../../../contexts/UserContext';

import './TrustSubmission.css';

import * as S from './TrustSubmission.styled';

const HEX_COLOUR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

const FILE_FIELDS = {
    app_icon: {
        label: 'App Icon',
        accept: 'image/png',
        uploadPath: 'app_icon',
        required: false,
        helperText: 'image format, 1024x1024',
        type: 'image-icon',
    },
    google_service_file_ios: {
        label: 'Google Service File iOS',
        accept: '.plist, application/xml, text/xml',
        uploadPath: 'google_service_file_ios',
        required: false,
        helperText: 'plist format',
    },
    google_service_file_android: {
        label: 'Google Service File Android',
        accept: '.json, application/json',
        uploadPath: 'google_service_file_android',
        required: false,
        helperText: 'json format',
    },
    web_login_image: {
        label: 'Web Login Image',
        accept: 'image/png, image/jpeg, image/jpg, image/gif',
        uploadPath: 'web_login_image',
        required: false,
        helperText: 'image format',
        type: 'image',
    },
    provisioning_profile: {
        label: 'Provisioning Profile',
        accept: '.mobileprovision, application/octet-stream',
        uploadPath: 'provisioning_profile',
        required: false,
        helperText: 'mobileprovision format',
    },
    ios_certificate: {
        label: 'iOS Certificate',
        accept: '.p12, application/x-pkcs12, application/octet-stream',
        uploadPath: 'ios_certificate',
        required: false,
        helperText: 'p12 format',
    },
};

export default class TrustSubmissionItem extends React.Component {
    static contextType = UserContext;

    constructor(props) {
        super(props);

        const isNew = !props.trustSubmissionItem || props.trustSubmissionItem.id === 'new';

        const trustSubmission = isNew
            ? {
                  id: 'new',
                  organisation_id: '',
                  app_id_ios: '',
                  app_id_android: '',
                  app_name: '',
                  apple_developer_team_id: '',
                  azure_client_id: '',
                  azure_tenant_id: '',
                  offset: '',
                  apple_code_signing_id: '',
                  provisioning_id: '',
                  app_icon: null,
                  google_service_file_ios: null,
                  google_service_file_android: null,
                  app_description: '',
                  app_theme_colour: '',
                  web_login_image: null,
                  provisioning_profile: null,
                  ios_certificate: null,
                  is_submitted: false,
                  is_approved: false,
              }
            : {
                  is_approved: false,
                  ...props.trustSubmissionItem,
              };

        this.state = {
            ...trustSubmission,
            isNew,
            isEditing: isNew,
            organisations: [],
        };
    }

    async componentDidMount() {
        const [organisations] = await Promise.all([this.getOrganisations(), this.reload()]);

        this.setState({
            organisations,
        });
    }

    getOrganisations = async () => {
        const resp = await get('cms/organisations', null, true);

        if (resp && resp.status === 401) {
            this.setState({ logout: true });
        }

        return resp && resp.organisations ? resp.organisations : resp && resp.data ? resp.data : [];
    };

    reload = async () => {};

    toggleEditingStatus = () => {
        const { isEditing, is_approved } = this.state;

        if (is_approved) {
            return this.setState({
                showMessage: {
                    message: 'Approved trust submissions cannot be edited.',
                    title: 'Trust Submission Approved',
                },
            });
        }

        if (isEditing) {
            this.saveTrustSubmission();
        } else {
            this.setState({ isEditing: true });
        }
    };

    onChangeTextField = (field, value, maxLength = 254) => {
        if (value && value.length > maxLength) return;

        this.setState({
            [field]: value,
        });
    };

    onFileChange = (field, file) => {
        this.setState({
            [field]: {
                file,
                original_name: file.name,
            },
        });
    };

    getFileName = (field) => {
        const value = this.state[field];

        if (!value) return '';

        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }

        return value.original_name || value.file_name || value.name || '';
    };

    getFileUrl = (field) => {
        const value = this.state[field];

        if (!value) return null;

        const fileId =
            typeof value === 'string' || typeof value === 'number'
                ? value
                : value.file_id || value.id || value.file_id_from_files;

        return fileId ? `cms/trust_submission/file/${fileId}` : null;
    };

    uploadFileField = async (field) => {
        const fileField = this.state[field];
        const config = FILE_FIELDS[field];

        if (!fileField) return null;

        if (fileField.id || fileField.file_id) {
            return fileField.file_id || fileField.id;
        }

        if (typeof fileField === 'string' || typeof fileField === 'number') {
            return fileField;
        }

        if (!fileField.file) return null;

        const fileResp = await upload(
            `cms/upload_file/${config.uploadPath}/trust_submission`,
            'file',
            fileField.file,
            true,
        );

        return fileResp && fileResp.file_id ? fileResp.file_id : null;
    };

    uploadFiles = async () => {
        const uploadedFiles = {};
        const fileFields = Object.keys(FILE_FIELDS);

        for (let index = 0; index < fileFields.length; index++) {
            const field = fileFields[index];
            const fileId = await this.uploadFileField(field);

            if (fileId) {
                uploadedFiles[`${field}_file_id`] = fileId;
            }
        }

        return uploadedFiles;
    };

    validate = async () => {
        const { organisation_id, app_theme_colour } = this.state;
        const organisationId = String(organisation_id || '').trim();

        if (!organisationId) {
            return 'Please enter an organisation ID.';
        }

        const organisations = await this.getOrganisations();

        const organisationExists = organisations.some(
            (organisation) => String(organisation.id) === organisationId,
        );

        this.setState({ organisations });

        if (!organisationExists) {
            return 'Could not find organisation with provided ID';
        }

        if (app_theme_colour && !HEX_COLOUR_REGEX.test(app_theme_colour)) {
            return 'Please include a valid app theme colour in hex format, for example #0055FF.';
        }

        return null;
    };

    hasFileValue = (field) => {
        const value = this.state[field];

        if (!value) return false;
        if (value.file || value.id || value.file_id || value.original_name || value.file_name || value.name)
            return true;

        return typeof value === 'string' && value.length > 0;
    };

    validateForSubmit = () => {
        const {
            organisation_id,
            app_id_ios,
            app_id_android,
            app_name,
            apple_developer_team_id,
            azure_client_id,
            azure_tenant_id,
            offset,
            apple_code_signing_id,
            provisioning_id,
            app_description,
            app_theme_colour,
        } = this.state;

        if (!String(organisation_id || '').trim()) {
            return 'Please enter an organisation ID.';
        }
        if (!app_id_ios) return 'Please include an iOS App ID.';
        if (!app_id_android) return 'Please include an Android App ID.';
        if (!app_name) return 'Please include an app name.';
        if (!apple_developer_team_id) return 'Please include an Apple Developer Team ID.';
        // if (!azure_client_id) return 'Please include an Azure Client ID.';
        // if (!azure_tenant_id) return 'Please include an Azure Tenant ID.';
        if (!offset) return 'Please include an offset.';
        if (!apple_code_signing_id) return 'Please include an Apple Code Signing ID.';
        if (!provisioning_id) return 'Please include a Provisioning ID.';
        if (!app_description) return 'Please include an app description.';

        if (!app_theme_colour || !HEX_COLOUR_REGEX.test(app_theme_colour)) {
            return 'Please include a valid app theme colour in hex format, for example #0055FF.';
        }

        const missingFileField = Object.keys(FILE_FIELDS).find((field) => !this.hasFileValue(field));

        if (missingFileField) {
            return `Please include a valid ${FILE_FIELDS[missingFileField].label}.`;
        }

        return null;
    };

    saveTrustSubmission = async ({ stayOnPage = false } = {}) => {
        if (this.state.is_approved) {
            return this.setState({
                showMessage: {
                    message: 'Approved trust submissions cannot be edited. Cancel approval first.',
                    title: 'Save Trust Submission',
                },
            });
        }

        const validationError = await this.validate();

        if (validationError) {
            return this.setState({
                showMessage: {
                    message: validationError,
                    title: 'Save Trust Submission',
                },
            });
        }

        const {
            organisation_id,
            app_id_ios,
            app_id_android,
            app_name,
            apple_developer_team_id,
            azure_client_id,
            azure_tenant_id,
            offset,
            apple_code_signing_id,
            provisioning_id,
            app_description,
            app_theme_colour,
        } = this.state;

        const uploadedFiles = await this.uploadFiles();

        const trustSubmissionItem = {
            organisation_id: String(organisation_id || '').trim(),
            app_id_ios,
            app_id_android,
            app_name,
            apple_developer_team_id,
            azure_client_id,
            azure_tenant_id,
            offset,
            apple_code_signing_id,
            provisioning_id,
            app_description,
            app_theme_colour,
            ...uploadedFiles,
        };

        const resp = this.state.isNew
            ? await post('cms/trust_submission', trustSubmissionItem, true)
            : await put(`cms/trust_submission/${this.state.id}`, trustSubmissionItem, true);

        if (resp && resp.success) {
            const savedId = resp.item ? resp.item.id : this.state.id;

            this.setState({
                isNew: false,
                isEditing: false,
                id: savedId,
            });

            if (!stayOnPage) {
                this.props.onBack();
            }

            return {
                success: true,
                id: savedId,
            };
        }

        this.setState({
            showMessage: {
                message: resp && resp.message ? resp.message : 'Could not save trust submission.',
                title: 'Save error',
            },
        });

        return {
            success: false,
        };
    };

    submitTrustSubmission = async () => {
        if (this.state.is_approved) {
            return this.setState({
                showMessage: {
                    message: 'Approved trust submissions cannot be submitted.',
                    title: 'Submit Trust Submission',
                },
            });
        }

        const validationError = this.validateForSubmit();

        if (validationError) {
            return this.setState({
                showMessage: {
                    message: validationError,
                    title: 'Submit Trust Submission',
                },
            });
        }

        const saveResult = await this.saveTrustSubmission({ stayOnPage: true });

        if (!saveResult || !saveResult.success || !saveResult.id) {
            return;
        }

        const resp = await put(`cms/trust_submission/${saveResult.id}/submitted`, { is_submitted: true }, true);

        if (resp && resp.success) {
            return this.setState({
                is_submitted: true,
                showMessage: {
                    message: 'Trust submission submitted successfully.',
                    title: 'Submit Trust Submission',
                },
            });
        }

        return this.setState({
            showMessage: {
                message: resp && resp.message ? resp.message : 'Could not submit trust submission.',
                title: 'Submit error',
            },
        });
    };

    cancelTrustSubmission = async () => {
        if (this.state.is_approved) {
            return this.setState({
                showMessage: {
                    message: 'Approved trust submissions cannot be changed. Cancel approval first.',
                    title: 'Cancel Trust Submission',
                },
            });
        }

        const resp = await put(`cms/trust_submission/${this.state.id}/submitted`, { is_submitted: false }, true);

        if (resp && resp.success) {
            return this.setState({
                is_submitted: false,
                showMessage: {
                    message: 'Trust submission cancelled successfully.',
                    title: 'Cancel Trust Submission',
                },
            });
        }

        return this.setState({
            showMessage: {
                message: resp && resp.message ? resp.message : 'Could not cancel trust submission.',
                title: 'Cancel error',
            },
        });
    };

    approveTrustSubmission = async () => {
        const resp = await put(`cms/trust_submission/${this.state.id}/approved`, { is_approved: true }, true);

        if (resp && resp.success) {
            return this.setState({
                is_approved: true,
                isEditing: false,
                showMessage: {
                    message: 'Trust submission approved successfully.',
                    title: 'Approve Trust Submission',
                },
            });
        }

        return this.setState({
            showMessage: {
                message: resp && resp.message ? resp.message : 'Could not approve trust submission.',
                title: 'Approval error',
            },
        });
    };

    cancelApproval = async () => {
        const resp = await put(`cms/trust_submission/${this.state.id}/approved`, { is_approved: false }, true);

        if (resp && resp.success) {
            return this.setState({
                is_approved: false,
                showMessage: {
                    message: 'Trust submission approval cancelled successfully.',
                    title: 'Cancel Approval',
                },
            });
        }

        return this.setState({
            showMessage: {
                message: resp && resp.message ? resp.message : 'Could not cancel approval.',
                title: 'Cancel approval error',
            },
        });
    };

    doDelete = async () => {
        const resp = await dodelete(`cms/trust_submission/${this.state.id}`, {}, true);

        if (resp && resp.success) {
            this.props.onBack();
        } else if (resp && resp.message) {
            setTimeout(
                () =>
                    this.setState({
                        showMessage: {
                            message: resp.message,
                            title: 'Delete Trust Submission',
                        },
                    }),
                500,
            );
        }
    };

    renderFileField = (field) => {
        const { isEditing, is_approved } = this.state;
        const canEdit = isEditing && !is_approved;
        const config = FILE_FIELDS[field];

        if (config.type === 'image') {
            return (
                <S.FileContainer key={field}>
                    <div className="mt-3 mb-3 w-50">
                        <div style={localStyle.typeTitle}>{config.label}</div>
                        <div style={localStyle.TypeSubtitle}>{config.helperText}</div>
                    </div>
                    <S.HorizontalSpacer size={20} />
                    <div className="group-edit-image-outer me-4">
                        <EditImage
                            editing={canEdit ? '1' : undefined}
                            imageid={this.state[field]}
                            onChange={(imageId) => this.setState({ [field]: imageId })}
                        />
                    </div>
                </S.FileContainer>
            );
        }

        if (config.type === 'image-icon') {
            return (
                <S.FileContainer key={field}>
                    <div className="mt-3 mb-3 w-50">
                        <div style={localStyle.typeTitle}>{config.label}</div>
                        <div style={localStyle.TypeSubtitle}>{config.helperText}</div>
                    </div>
                    <S.HorizontalSpacer size={20} />
                    <div className="group-edit-image-outer me-4">
                        <EditImage
                            editing={canEdit ? '1' : undefined}
                            imageid={this.state.app_icon}
                            requiredWidth={1024}
                            requiredHeight={1024}
                            dimensionErrorMessage="App Icon must be exactly 1024x1024px."
                            onChange={(imageId) => this.setState({ app_icon: imageId })}
                        />
                    </div>
                </S.FileContainer>
            );
        }

        return (
            <S.FileContainer key={field}>
                <div className="mt-3 mb-3 w-50">
                    <div style={localStyle.typeTitle}>{config.label}</div>
                    <div style={localStyle.TypeSubtitle}>{config.helperText}</div>
                </div>
                <S.HorizontalSpacer size={20} />
                <EditBrowseTrust
                    editing={canEdit ? '1' : undefined}
                    onFileChange={(selectedFile) => this.onFileChange(field, selectedFile)}
                    fileName={this.getFileName(field)}
                    fileUrl={this.getFileUrl(field)}
                    acceptFileTypes={config.accept}
                />
            </S.FileContainer>
        );
    };

    render() {
        const {
            isEditing,
            id,
            organisation_id,
            app_id_ios,
            app_id_android,
            app_name,
            apple_developer_team_id,
            azure_client_id,
            azure_tenant_id,
            offset,
            apple_code_signing_id,
            provisioning_id,
            app_description,
            app_theme_colour,
            is_submitted,
            is_approved,
        } = this.state;

        const user = (this.context && this.context.user) || {};
        const isMasterAdmin = user.master_admin;
        const canEdit = isEditing && !is_approved;

        return (
            <div className="maincontainer">
                <BackRow
                    onBack={this.props.onBack}
                    {...(!this.state.isNew
                        ? {
                              buttonTitle: 'DELETE ITEM',
                              onDelete: () => this.setState({ showDeleteDialog: true }),
                          }
                        : {})}
                />

                <Card title="Trust Submission" editing={canEdit} onToggle={this.toggleEditingStatus}>
                    <S.VerticalSpacer size={35} />

                    {is_approved ? (
                        <div className="alert alert-success w-100" role="alert">
                            This trust submission has been approved. It cannot be edited or submitted unless a master
                            admin cancels the approval.
                        </div>
                    ) : null}

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="ID"
                            editing={false}
                            value={id === 'new' ? 'Auto-generated' : id}
                            helpText="Generated automatically when the trust submission is created."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Organisation ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('organisation_id', value)}
                            value={organisation_id}
                            placeholder="Enter organisation ID"
                            helpText="Enter the organisation ID. It will be validated when saving."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="iOS App ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('app_id_ios', value)}
                            value={app_id_ios}
                            helpText="The iOS bundle identifier, for example com.company.app."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Android App ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('app_id_android', value)}
                            value={app_id_android}
                            helpText="The Android application ID/package name, for example com.company.app."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="App Name"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('app_name', value)}
                            value={app_name}
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Apple Developer Team ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('apple_developer_team_id', value)}
                            value={apple_developer_team_id}
                            helpText="Apple Developer Team ID used for signing the iOS app."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Offset"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('offset', value)}
                            value={offset}
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Apple Code Signing ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('apple_code_signing_id', value)}
                            value={apple_code_signing_id}
                            helpText="Certificate ID, i.e. Apple Distribution: South Tees NHS Foundation Trust (WV9ZVHY632)"
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Provisioning ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('provisioning_id', value)}
                            value={provisioning_id}
                            helpText="Apple developer team ID and provisioning profile ID, i.e. WV9ZVHY632/UHTees_Production"
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Azure Client ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('azure_client_id', value)}
                            value={azure_client_id}
                            helpText="Azure application/client ID used for authentication configuration."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="Azure Tenant ID"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('azure_tenant_id', value)}
                            value={azure_tenant_id}
                            helpText="Azure tenant/directory ID associated with the app registration."
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="App Description"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('app_description', value, 1000)}
                            value={app_description}
                        />

                        <EditText
                            outerClassName="mt-3 mb-3 mr-2 w-100"
                            label="App Theme Colour"
                            editing={canEdit}
                            onChange={(value) => this.onChangeTextField('app_theme_colour', value)}
                            value={app_theme_colour}
                            placeholder="#0055FF"
                            helpText="Hex colour code only, for example #0055FF or #05F."
                        />
                    </div>

                    {Object.keys(FILE_FIELDS).map((field) => this.renderFileField(field))}

                    {!this.state.isNew ? (
                        <S.BottomFileContainer>
                            <button
                                className="btn btn-primary knowledgebase-btn"
                                disabled={is_submitted || is_approved}
                                onClick={this.submitTrustSubmission}
                            >
                                {is_submitted ? 'SUBMITTED' : 'SUBMIT'}
                            </button>

                            {is_submitted && !is_approved ? (
                                <button
                                    className="btn btn-outline-primary knowledgebase-btn"
                                    style={{ marginLeft: 12 }}
                                    onClick={this.cancelTrustSubmission}
                                >
                                    CANCEL SUBMISSION
                                </button>
                            ) : null}

                            {is_approved ? (
                                <div style={{ marginLeft: 12, fontWeight: 600 }}>
                                    APPROVED
                                </div>
                            ) : null}
                        </S.BottomFileContainer>
                    ) : null}

                    {!this.state.isNew && isMasterAdmin ? (
                        <S.BottomFileContainer>
                            <div className="mt-3 mb-3 w-100">
                                <div style={localStyle.typeTitle}>Approval</div>
                                <div style={localStyle.TypeSubtitle}>
                                    Master admins can approve this trust submission. Once approved, it cannot be edited
                                    or submitted.
                                </div>
                            </div>

                            {is_approved ? (
                                <button
                                    className="btn btn-outline-primary knowledgebase-btn"
                                    onClick={this.cancelApproval}
                                >
                                    CANCEL
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary knowledgebase-btn"
                                    onClick={this.approveTrustSubmission}
                                >
                                    APPROVE
                                </button>
                            )}
                        </S.BottomFileContainer>
                    ) : null}

                    {this.state.showMessage ? (
                        <MessageDialog
                            message={this.state.showMessage.message}
                            title={this.state.showMessage.title}
                            onClose={() => this.setState({ showMessage: null })}
                        />
                    ) : null}

                    {this.state.showDeleteDialog ? (
                        <DeleteDialog
                            item="Trust Submission"
                            onSuccess={this.doDelete}
                            onClose={() => this.setState({ showDeleteDialog: false })}
                        />
                    ) : null}
                </Card>
            </div>
        );
    }
}

const localStyle = {
    subtitle: {
        fontSize: '15px',
        fontWeight: '400',
        letterSpacing: '-0.15px',
        color: '#000',
    },
    typeTitle: {
        fontSize: '15px',
        fontWeight: '400',
        letterSpacing: '-0.15px',
        color: '#000',
        marginLeft: 8,
    },
    TypeSubtitle: {
        fontSize: '15px',
        fontWeight: '400',
        letterSpacing: '-0.15px',
        color: '#505A62',
        textAlign: 'left',
        marginLeft: 8,
        marginTop: 3,
    },
};
